import React, { useEffect, useState } from 'react';
import { ArrowUp, ArrowDown, Edit2, Check, X, RotateCcw, Save } from 'lucide-react';
import api from '../api/client';
import { useMenu, ricaricaMenu } from './menuStaff';
import { CATALOGO_PORTALE, ordinaVociPortale } from './menuPortale';

// Impostazioni → Menu laterale → Portale utenti / Portale allenatori (solo ROOT, 23/10/2026):
// ordine e nomi soltanto. Accendere o spegnere una voce resta in Impostazioni → Ruoli.
const MAX_NOME = 40;

export default function EditorMenuPortale({ ambito }) {
  const catalogo = CATALOGO_PORTALE[ambito];
  const originale = Object.fromEntries(catalogo.map(v => [v.path, v.label]));
  const salvato = useMenu(ambito);
  const daSalvato = () => {
    const voci = ordinaVociPortale(catalogo, salvato);
    return { ordine: voci.map(v => v.path), nomi: { ...(salvato?.nomi || {}) } };
  };
  const [stato, setStato] = useState(daSalvato);
  const [modificato, setModificato] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [rinomina, setRinomina] = useState(null); // { path, valore }

  useEffect(() => { setStato(daSalvato()); setModificato(false); setRinomina(null); }, [salvato, ambito]);

  const cambia = (fn) => { setStato(s => { const n = { ordine: [...s.ordine], nomi: { ...s.nomi } }; fn(n); return n; }); setModificato(true); };
  const muovi = (i, d) => cambia(n => { const j = i + d; if (j < 0 || j >= n.ordine.length) return; [n.ordine[i], n.ordine[j]] = [n.ordine[j], n.ordine[i]]; });
  const icona = (p) => catalogo.find(v => v.path === p).icon;

  const confermaRinomina = () => {
    const v = rinomina.valore.trim();
    if (v.length > MAX_NOME) { alert(`Al massimo ${MAX_NOME} caratteri`); return; }
    cambia(n => { if (!v || v === originale[rinomina.path]) delete n.nomi[rinomina.path]; else n.nomi[rinomina.path] = v; });
    setRinomina(null);
  };

  const salva = async () => {
    setSalvando(true);
    try { await api.put(`/menu/${ambito}`, { voci: stato.ordine.map(path => ({ tipo: 'voce', path })), nomi: stato.nomi }); await ricaricaMenu(ambito); }
    catch (e) { alert(e.response?.data?.error || 'Salvataggio non riuscito'); }
    finally { setSalvando(false); }
  };

  const ripristina = async () => {
    if (!window.confirm('Tornare al menu originale di questo portale? Si perdono ordine e nomi cambiati.')) return;
    setSalvando(true);
    try { await api.delete(`/menu/${ambito}`); await ricaricaMenu(ambito); }
    catch (e) { alert(e.response?.data?.error || 'Ripristino non riuscito'); }
    finally { setSalvando(false); }
  };

  return (
    <div>
      <div style={st.spiega}>
        Qui decidi <b>ordine</b> e <b>nomi</b> del menu del {ambito === 'UTENTE' ? 'portale utenti' : 'portale allenatori'}. Vale per tutti{ambito === 'ALLENATORE' ? ', anche per lo staff in modalità allenatore' : ''}.
        Per <b>accendere o spegnere</b> una voce usa Impostazioni → Ruoli; "Diventa socio" sparisce sempre per chi è già socio.
      </div>

      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', margin:'14px 0' }}>
        <button style={{ ...st.btnPrim, opacity: !modificato || salvando ? .45 : 1 }} disabled={!modificato || salvando} onClick={salva}>
          <Save size={13}/> {salvando ? 'SALVO…' : 'SALVA MENU'}
        </button>
        <button style={{ ...st.btn, opacity: !modificato || salvando ? .45 : 1 }} disabled={!modificato || salvando} onClick={() => { setStato(daSalvato()); setModificato(false); setRinomina(null); }}>
          <X size={13}/> ANNULLA MODIFICHE
        </button>
        <button style={{ ...st.btn, marginLeft:'auto' }} disabled={salvando || !salvato} onClick={ripristina}>
          <RotateCcw size={13}/> RIPRISTINA MENU ORIGINALE
        </button>
      </div>
      {modificato && <div style={{ fontSize:'var(--font-size-xs)', color:'#F59E0B', marginBottom:'10px', fontWeight:700 }}>Modifiche non ancora salvate</div>}

      <div style={{ display:'flex', flexDirection:'column', gap:'4px', maxWidth:'720px' }}>
        {stato.ordine.map((p, i) => {
          const Icona = icona(p);
          const nome = stato.nomi[p] || originale[p];
          return (
            <div key={p} style={st.riga}>
              <Icona size={14} color="var(--text-dim)" style={{ flexShrink:0 }}/>
              {rinomina?.path === p ? (
                <span style={{ display:'flex', gap:'4px', alignItems:'center', flex:'1 1 220px', minWidth:0 }}>
                  <input autoFocus value={rinomina.valore} maxLength={MAX_NOME} placeholder={originale[p]}
                    onChange={e => setRinomina(r => ({ ...r, valore: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') confermaRinomina(); if (e.key === 'Escape') setRinomina(null); }}
                    style={{ ...st.input, flex:1, minWidth:0 }}/>
                  <button style={st.ico} title="Conferma" onClick={confermaRinomina}><Check size={14} color="#22C55E"/></button>
                  <button style={st.ico} title="Annulla" onClick={() => setRinomina(null)}><X size={14}/></button>
                </span>
              ) : (
                <span style={{ flex:'1 1 150px', minWidth:0 }}>
                  <span style={st.nome}>{nome}</span>
                  {stato.nomi[p] && <span style={st.orig}> (era: {originale[p]})</span>}
                </span>
              )}
              {rinomina?.path !== p && <button style={st.ico} title="Rinomina" onClick={() => setRinomina({ path: p, valore: nome })}><Edit2 size={13}/></button>}
              <button style={st.ico} title="Su" disabled={i === 0} onClick={() => muovi(i, -1)}><ArrowUp size={14} style={{ opacity: i === 0 ? .3 : 1 }}/></button>
              <button style={st.ico} title="Giù" disabled={i === stato.ordine.length - 1} onClick={() => muovi(i, 1)}><ArrowDown size={14} style={{ opacity: i === stato.ordine.length - 1 ? .3 : 1 }}/></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const st = {
  spiega:  { fontSize:'var(--font-size-sm)', color:'var(--text-muted)', lineHeight:1.5, maxWidth:'720px' },
  riga:    { display:'flex', alignItems:'center', gap:'6px', padding:'6px 8px', borderRadius:'8px', background:'var(--bg-card)', border:'1px solid var(--border)', flexWrap:'wrap' },
  nome:    { fontSize:'var(--font-size-sm)', fontWeight:600, color:'var(--text)' },
  orig:    { fontSize:'var(--font-size-xs)', color:'var(--text-dim)', fontWeight:400 },
  ico:     { background:'transparent', border:'none', cursor:'pointer', padding:'4px', display:'flex', color:'var(--text-muted)' },
  input:   { fontSize:'var(--font-size-sm)', padding:'4px 8px', borderRadius:'6px', border:'1px solid var(--border)', background:'var(--bg-input)', color:'var(--text)' },
  btn:     { display:'flex', alignItems:'center', gap:'6px', padding:'8px 12px', borderRadius:'8px', border:'1px solid var(--border)', background:'transparent', color:'var(--text)', fontSize:'var(--font-size-xs)', fontWeight:700, letterSpacing:'.5px', cursor:'pointer' },
  btnPrim: { display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'8px', border:'none', background:'#F4600C', color:'#fff', fontSize:'var(--font-size-xs)', fontWeight:700, letterSpacing:'.5px', cursor:'pointer' },
};
